import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './ReceberAportes.css';

/**
 * ReceberAportes.js — entrada de dinheiro na conta Stark Bank (modelo Banking)
 * ---------------------------------------------------------------------------
 * A Stark não "puxa" de TOTVS/Itaú/Santander — o dinheiro ENTRA quando esses
 * sistemas empurram. Esta tela expõe os meios de recebimento e reconcilia:
 *   • Chave Pix da conta (dictKey)  → recebimento direto de terceiros
 *   • Gerar cobrança Pix (invoice)  → QR/link pra um aporte específico
 *   • Gerar boleto (boleto)         → aporte via boleto
 *   • Recebimentos (deposit)        → o que caiu, com pagador/valor/banco
 * (Pix Automático / invoicePull fica pra um próximo passo.)
 *
 * ===========================================================================
 * 🔴 AMBIENTE — por que esta tela é a MAIS sensível do módulo
 * ===========================================================================
 * Aqui não se apenas *exibe* dado do ambiente errado: aqui se **entrega** dado
 * do ambiente errado para terceiros.
 *
 *   - Uma chave Pix de sandbox copiada e colada no ERP do cliente significa
 *     dinheiro real enviado para uma chave que não é a conta de produção.
 *   - Um boleto de sandbox enviado a um pagador é um boleto que ninguém
 *     concilia, e que queima a confiança de quem pagou.
 *
 * Por isso, nesta tela o ambiente é tratado como informação de primeira
 * classe, não como detalhe:
 *
 *  1. NADA é renderizado antes do GET /account confirmar o ambiente. Sem
 *     "pintar primeiro e corrigir depois".
 *  2. Selo PRODUÇÃO/SANDBOX no cabeçalho, e de novo dentro do bloco da chave
 *     Pix e do modal de cobrança — onde a decisão de copiar/enviar acontece.
 *  3. Em sandbox, a chave Pix aparece marcada como chave de TESTE, e o botão
 *     de copiar diz isso explicitamente.
 *  4. Toda ação que gera artefato entregável (cobrança Pix, boleto) confere se
 *     o escopo ainda é o mesmo de quando o modal abriu.
 *
 * ===========================================================================
 * 🛡️ BLINDAGEM
 * ===========================================================================
 *  - AbortController em todo fetch, cancelado no unmount (a versão anterior
 *    disparava `pix-keys` sem await e sem flag: setState em componente
 *    desmontado, e resposta atrasada sobrescrevendo estado novo).
 *  - Guarda de duplo submit em toda geração de cobrança — clicar duas vezes
 *    criava DUAS cobranças reais.
 *  - Validação local antes de ir pra API: valor, CPF/CNPJ (11/14 dígitos),
 *    CEP (8 dígitos), UF na lista real, vencimento não no passado.
 *  - `copy()` deixa de falhar em silêncio: entregar chave Pix "achando que
 *    copiou" é pior que erro visível.
 *  - Esc e trava de scroll nos modais.
 *  - 403 do backend vira mensagem clara; 409 explica conta desconectada.
 * ---------------------------------------------------------------------------
 */

const fmtBRL = (v) => (parseFloat(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PIX_TYPE_LABEL = { cpf: 'CPF', cnpj: 'CNPJ', email: 'E-mail', phone: 'Telefone', evp: 'Chave aleatória' };

const UFS = new Set([
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]);

const ENV_LABEL = { production: 'PRODUÇÃO', sandbox: 'SANDBOX' };
const envLabelOf = (env) => ENV_LABEL[env] || String(env || '').toUpperCase() || '—';

const VALOR_MAX = 1_000_000;      // espelha o teto do backend
const BOLETO_MIN = 2;             // mínimo da Stark para boleto
const DEPOSITS_MAX = 500;         // teto acumulado na tela (evita lista infinita)

const asArray = (v) => (Array.isArray(v) ? v : []);
const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '');

const apiErr = (e, fallback = 'Falha na comunicação com o servidor.') => {
    if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') return null;
    const status = e?.response?.status;
    if (status === 403) return 'Seu perfil não tem permissão para esta ação. Fale com um administrador.';
    if (status === 409) return e?.response?.data?.error || 'A conta Stark Bank não está conectada.';
    if (status === 503) return e?.response?.data?.error || 'Serviço indisponível no momento. Tente novamente.';
    return e?.response?.data?.error || e?.message || fallback;
};

/** Valor monetário em faixa. Aceita "1.234,56" e "1234.56". */
const parseValor = (raw, { min = 0.01, max = VALOR_MAX } = {}) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const s = String(raw).trim().replace(/\s/g, '');
    const norm = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
    const n = Number(norm);
    if (!Number.isFinite(n) || n < min || n > max) return null;
    return Math.round(n * 100) / 100;
};

const taxIdValido = (raw) => {
    const d = onlyDigits(raw);
    return d.length === 11 || d.length === 14;
};

/** Vencimento precisa ser hoje ou no futuro — a Stark recusa data passada. */
const vencimentoValido = (iso) => {
    if (!iso) return true; // opcional
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const d = new Date(`${iso}T00:00:00`);
    return !Number.isNaN(d.getTime()) && d >= hoje;
};

/**
 * ESCOPO DE AMBIENTE — mesma identidade usada na tela de cartões.
 * Vive no módulo para que um modal aberto possa comparar com o estado atual.
 */
const SCOPE = { current: null };
const scopeOf = (account) => (account
    ? `${account.environment || '?'}::${account.stark_project_id || '?'}`
    : null);

const escopoAindaVale = (scopeAoAbrir) => {
    if (!scopeAoAbrir || !SCOPE.current) return true;
    return SCOPE.current === scopeAoAbrir;
};

const avisarEscopoMudou = () => {
    toast.error('O ambiente da conta Stark Bank mudou. Recarregue a tela antes de gerar cobranças.', { toastId: 'ra-scope' });
};

/** Cópia com feedback real: silenciar falha aqui entrega chave Pix errada. */
const copiar = async (text, label) => {
    const valor = String(text ?? '');
    if (!valor) { toast.error(`Nada para copiar em ${label}.`); return false; }
    if (!navigator.clipboard?.writeText) {
        toast.error('Seu navegador não permite copiar automaticamente. Selecione e copie à mão.');
        return false;
    }
    try {
        await navigator.clipboard.writeText(valor);
        toast.info(`${label} copiado.`);
        return true;
    } catch {
        toast.error(`Não foi possível copiar ${label}. Selecione e copie à mão.`);
        return false;
    }
};

// ── Hooks de UI ─────────────────────────────────────────────────────────────
let SCROLL_LOCKS = 0;
function useBodyScrollLock(active) {
    useEffect(() => {
        if (!active) return undefined;
        SCROLL_LOCKS += 1;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            SCROLL_LOCKS = Math.max(0, SCROLL_LOCKS - 1);
            if (SCROLL_LOCKS === 0) document.body.style.overflow = prev || '';
        };
    }, [active]);
}

function useEscapeKey(handler, active = true) {
    const ref = useRef(handler);
    ref.current = handler;
    useEffect(() => {
        if (!active) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            ref.current?.();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [active]);
}

/** Selo de ambiente, reaproveitado em cabeçalho, card e modal. */
function EnvBadge({ environment, className = '' }) {
    if (!environment) return null;
    const prod = environment === 'production';
    return (
        <span className={`ra-env-badge ${prod ? 'is-prod' : 'is-sbx'} ${className}`}>
            ● {envLabelOf(environment)}
        </span>
    );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export default function ReceberAportes() {
    // `hydrated` só vira true depois do /account responder. Nada de chave Pix
    // ou botão de cobrança em tela antes de saber o ambiente.
    const [hydrated, setHydrated] = useState(false);
    const [connected, setConnected] = useState(false);
    const [account, setAccount] = useState(null);

    const [pixKeys, setPixKeys] = useState(null);
    const [deposits, setDeposits] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loadingDeposits, setLoadingDeposits] = useState(false);
    const [showCharge, setShowCharge] = useState(false);

    const aliveRef = useRef(true);
    const inFlightRef = useRef(null);
    useEffect(() => () => {
        aliveRef.current = false;
        inFlightRef.current?.abort();
    }, []);

    const environment = account?.environment || null;
    const isProd = environment === 'production';
    const scope = scopeOf(account);

    const loadDeposits = useCallback(async (next = null, signal) => {
        if (!aliveRef.current) return;
        setLoadingDeposits(true);
        try {
            const { data } = await api.get('/api/baas/account/deposits', {
                params: { limit: 20, ...(next ? { cursor: next } : {}) },
                signal,
            });
            if (!aliveRef.current) return;
            const lote = asArray(data?.deposits);
            setDeposits((prev) => {
                const merged = next ? [...prev, ...lote] : lote;
                // Teto de sanidade: paginar sem fim viraria lista de milhares de
                // linhas no DOM, e ninguém audita recebimento rolando 3 mil itens.
                return merged.slice(0, DEPOSITS_MAX);
            });
            setCursor(data?.cursor || null);
        } catch (e) {
            const msg = apiErr(e);
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setLoadingDeposits(false);
        }
    }, []);

    const load = useCallback(async () => {
        inFlightRef.current?.abort();
        const controller = new AbortController();
        inFlightRef.current = controller;

        try {
            const { data } = await api.get('/api/baas/account', { signal: controller.signal });
            if (!aliveRef.current) return;

            const nextAccount = data?.account || null;
            const isConnected = !!data?.connected;
            const nextScope = scopeOf(nextAccount);

            // Ambiente mudou desde a última visita: o que estava em tela é de
            // outro ambiente e não pode ser aproveitado.
            if (SCOPE.current && SCOPE.current !== nextScope) {
                setPixKeys(null);
                setDeposits([]);
                setCursor(null);
                setShowCharge(false);
            }
            SCOPE.current = nextScope;

            setAccount(nextAccount);
            setConnected(isConnected);

            if (isConnected) {
                // ⚠️ A versão anterior disparava isto sem await e sem flag de
                // vida: gerava setState em componente desmontado.
                api.get('/api/baas/account/pix-keys', { signal: controller.signal })
                    .then(({ data: k }) => { if (aliveRef.current) setPixKeys(k || { available: false, keys: [] }); })
                    .catch((e) => {
                        if (!aliveRef.current) return;
                        const aborted = e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED';
                        if (!aborted) setPixKeys({ available: false, keys: [] });
                    });
                loadDeposits(null, controller.signal);
            } else {
                setPixKeys(null);
                setDeposits([]);
                setCursor(null);
            }
        } catch (e) {
            const msg = apiErr(e);
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (inFlightRef.current === controller) inFlightRef.current = null;
            if (aliveRef.current && !controller.signal.aborted) setHydrated(true);
        }
    }, [loadDeposits]);

    useEffect(() => { load(); }, [load]);

    useBodyScrollLock(showCharge);

    const totalRecebido = useMemo(
        () => asArray(deposits).reduce((s, d) => s + (parseFloat(d?.amount) || 0), 0),
        [deposits],
    );

    const pixKeysList = asArray(pixKeys?.keys);

    return (
        <div className="ra-wrap">
            <header className="ra-header">
                <div>
                    <h1>
                        Receber Aportes
                        {hydrated && <EnvBadge environment={environment} />}
                    </h1>
                    <p className="ra-sub">
                        {hydrated
                            ? `Entrada de dinheiro na conta Stark Bank — Pix, boleto e reconciliação de recebimentos · ${envLabelOf(environment)}`
                            : 'Confirmando ambiente da conta...'}
                    </p>
                </div>
                {hydrated && connected && (
                    <button className="ra-btn-primary" onClick={() => setShowCharge(true)}>+ Gerar cobrança</button>
                )}
            </header>

            {/* Aviso de sandbox em destaque: aqui se ENTREGA artefato a terceiro,
                então o alerta é mais forte que na tela de cartões. */}
            {hydrated && connected && !isProd && (
                <div className="ra-env-alert">
                    <span className="ra-env-alert-icon" aria-hidden="true">⚠️</span>
                    <div>
                        <strong>Você está no ambiente de TESTES (Sandbox).</strong>
                        <p>
                            A chave Pix, as cobranças e os boletos gerados aqui <u>não recebem dinheiro real</u>.
                            Não entregue nada desta tela a um cliente ou ao ERP dele. Para operar de verdade,
                            troque o ambiente na tela "Conta Digital Stark Bank".
                        </p>
                    </div>
                </div>
            )}

            {!hydrated ? (
                <div className="ra-loading">Carregando...</div>
            ) : !connected ? (
                <div className="ra-notice">
                    <span className="ra-notice-icon">🔌</span>
                    <div>
                        <strong>Nenhuma conta Stark Bank conectada.</strong>
                        <p>Conecte a conta na tela "Conta" antes de receber aportes.</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="ra-grid">
                        {/* ── Chave Pix ─────────────────────────────────────── */}
                        <section className={`ra-card ra-pix-card ${isProd ? '' : 'ra-pix-card--sbx'}`}>
                            <div className="ra-card-head">
                                <h3>Chave Pix de recebimento</h3>
                                <EnvBadge environment={environment} />
                            </div>

                            {isProd ? (
                                <p className="ra-hint">
                                    Entregue esta chave ao sistema/banco de origem (TOTVS, Itaú, Santander...) para receber
                                    Pix direto na conta. Cada entrada aparece em "Recebimentos" abaixo.
                                </p>
                            ) : (
                                <p className="ra-hint ra-hint--danger">
                                    <strong>Chave de teste.</strong> Não entregue ao cliente nem cadastre em ERP:
                                    Pix enviado para uma chave de sandbox não chega na conta de produção.
                                </p>
                            )}

                            {pixKeys == null ? (
                                <div className="ra-empty">Carregando chaves...</div>
                            ) : pixKeys.available && pixKeysList.length > 0 ? (
                                <div className="ra-pix-list">
                                    {pixKeysList.map((k) => (
                                        <div key={k.id} className={`ra-pix-row ${isProd ? '' : 'is-sbx'}`}>
                                            <div className="ra-pix-info">
                                                <span className="ra-pix-type">{PIX_TYPE_LABEL[k.type] || k.type || '—'}</span>
                                                <code className="ra-pix-key">{k.id}</code>
                                                {!isProd && <span className="ra-pix-flag">chave de teste</span>}
                                            </div>
                                            <button
                                                className={isProd ? 'ra-btn-ghost' : 'ra-btn-ghost ra-btn-ghost--warn'}
                                                onClick={() => copiar(k.id, isProd ? 'Chave Pix' : 'Chave Pix de TESTE')}
                                                title={isProd ? 'Copiar chave Pix' : 'Atenção: esta é uma chave de sandbox'}
                                            >
                                                {isProd ? 'Copiar' : 'Copiar (teste)'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="ra-empty">
                                    Nenhuma chave Pix cadastrada nesta conta. Cadastre uma pelo Web Banking da Stark Bank para receber direto.
                                </div>
                            )}
                        </section>

                        {/* ── Resumo ────────────────────────────────────────── */}
                        <section className="ra-card ra-summary-card">
                            <div className="ra-card-head"><h3>Recebido (nesta lista)</h3></div>
                            <span className="ra-summary-value">{fmtBRL(totalRecebido)}</span>
                            <span className="ra-summary-sub">
                                {deposits.length} recebimento{deposits.length !== 1 ? 's' : ''}
                                {deposits.length >= DEPOSITS_MAX ? ' (limite da tela)' : ''}
                            </span>
                            <button className="ra-btn-outline" onClick={() => loadDeposits()} disabled={loadingDeposits}>
                                {loadingDeposits ? 'Atualizando...' : '⟳ Atualizar recebimentos'}
                            </button>
                        </section>
                    </div>

                    {/* ── Recebimentos ──────────────────────────────────────── */}
                    <section className="ra-card">
                        <div className="ra-card-head">
                            <h3>Recebimentos</h3>
                            <span className="ra-hint-inline">entradas de Pix/TED de terceiros · {envLabelOf(environment)}</span>
                        </div>
                        {deposits.length === 0 && !loadingDeposits ? (
                            <div className="ra-empty">Nenhum recebimento ainda. Quando cair um Pix/TED na conta, ele aparece aqui.</div>
                        ) : (
                            <div className="ra-table-wrap">
                                <table className="ra-table">
                                    <thead>
                                        <tr><th>Data</th><th>Pagador</th><th>Banco</th><th>Tipo</th><th className="ra-right">Valor</th></tr>
                                    </thead>
                                    <tbody>
                                        {deposits.map((d, i) => (
                                            <tr key={d.id || `dep-${i}`}>
                                                <td className="ra-nowrap">{fmtDate(d.created)}</td>
                                                <td>
                                                    <div className="ra-payer">
                                                        <strong>{d.name || '—'}</strong>
                                                        <small>{d.taxId || ''}</small>
                                                    </div>
                                                </td>
                                                <td className="ra-nowrap">{d.bankCode || '—'}</td>
                                                <td><span className="ra-type-pill">{String(d.type || '').toUpperCase() || '—'}</span></td>
                                                <td className="ra-right ra-pos">+ {fmtBRL(d.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {cursor && deposits.length < DEPOSITS_MAX && (
                            <button className="ra-btn-ghost ra-loadmore" disabled={loadingDeposits} onClick={() => loadDeposits(cursor)}>
                                {loadingDeposits ? 'Carregando...' : 'Carregar mais'}
                            </button>
                        )}
                    </section>
                </>
            )}

            {showCharge && (
                <ChargeModal
                    environment={environment}
                    scope={scope}
                    onClose={() => setShowCharge(false)}
                    onDone={() => loadDeposits()}
                />
            )}
        </div>
    );
}

// =============================================================================
// MODAL — GERAR COBRANÇA (Pix invoice | Boleto)
// =============================================================================
function ChargeModal({ environment, scope, onClose, onDone }) {
    const [tab, setTab] = useState('pix');
    const [travado, setTravado] = useState(false); // enquanto gera, não fecha

    const fechar = useCallback(() => { if (!travado) onClose(); }, [travado, onClose]);
    useEscapeKey(fechar);

    const isProd = environment === 'production';

    return (
        <div className="ra-modal-overlay" onClick={fechar}>
            <div
                className="ra-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Gerar cobrança de aporte"
            >
                <button className="ra-modal-close" onClick={fechar} disabled={travado} aria-label="Fechar">×</button>
                <h3>
                    Gerar cobrança de aporte
                    <EnvBadge environment={environment} />
                </h3>

                {/* O artefato gerado aqui vai para as mãos de outra pessoa. O
                    aviso precisa estar no momento da decisão, não só no topo
                    da página — quem abriu o modal já rolou para longe dele. */}
                {!isProd && (
                    <div className="ra-modal-warn">
                        <strong>Sandbox:</strong> a cobrança gerada aqui é de teste e nunca vai ser paga de verdade.
                        Não envie ao pagador.
                    </div>
                )}

                <div className="ra-tabs">
                    <button
                        className={`ra-tab ${tab === 'pix' ? 'active' : ''}`}
                        onClick={() => setTab('pix')}
                        disabled={travado}
                    >
                        Pix
                    </button>
                    <button
                        className={`ra-tab ${tab === 'boleto' ? 'active' : ''}`}
                        onClick={() => setTab('boleto')}
                        disabled={travado}
                    >
                        Boleto
                    </button>
                </div>

                {tab === 'pix'
                    ? <PixCharge environment={environment} scope={scope} onDone={onDone} onBusy={setTravado} />
                    : <BoletoCharge environment={environment} scope={scope} onDone={onDone} onBusy={setTravado} />}
            </div>
        </div>
    );
}

function PixCharge({ environment, scope, onDone, onBusy }) {
    const [f, setF] = useState({ amount: '', name: '', taxId: '', description: '' });
    const [creating, setCreating] = useState(false);
    const [result, setResult] = useState(null);
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);
    useEffect(() => { onBusy?.(creating); }, [creating, onBusy]);

    const submit = async (e) => {
        e.preventDefault();
        // Sem esta guarda, dois cliques criavam DUAS cobranças reais.
        if (creating) return;

        // Cobrança é artefato entregável: se o ambiente virou desde que o modal
        // abriu, abortar é a única saída correta.
        if (!escopoAindaVale(scope)) { avisarEscopoMudou(); return; }

        const valor = parseValor(f.amount, { min: 0.01, max: VALOR_MAX });
        if (valor === null) { toast.error(`Informe um valor entre R$ 0,01 e R$ ${VALOR_MAX.toLocaleString('pt-BR')}.`); return; }
        if (!f.name.trim()) { toast.error('Informe o nome do pagador.'); return; }
        if (!taxIdValido(f.taxId)) { toast.error('CPF/CNPJ do pagador inválido (11 ou 14 dígitos).'); return; }

        setCreating(true);
        try {
            const { data } = await api.post('/api/baas/account/invoice', {
                amount: valor,
                name: f.name.trim(),
                tax_id: onlyDigits(f.taxId),
                description: f.description.trim() || undefined,
            });
            if (!aliveRef.current) return;
            setResult(data);
            onDone?.();
        } catch (e2) {
            const msg = apiErr(e2, 'Não foi possível gerar a cobrança Pix.');
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setCreating(false);
        }
    };

    if (result) {
        const isProd = environment === 'production';
        return (
            <div className="ra-result">
                <h4>Cobrança Pix gerada! {!isProd && <span className="ra-result-sbx">(teste)</span>}</h4>
                <span className="ra-result-amount">{fmtBRL(result.amount)}</span>
                <div className="ra-code-box">
                    <code>{String(result.brcode || '').slice(0, 44)}{result.brcode ? '...' : '—'}</code>
                    <button
                        className="ra-btn-ghost"
                        onClick={() => copiar(result.brcode, isProd ? 'Pix Copia e Cola' : 'Pix Copia e Cola de TESTE')}
                        disabled={!result.brcode}
                    >
                        Copiar
                    </button>
                </div>
                {result.pdf && (
                    <a href={result.pdf} target="_blank" rel="noreferrer noopener" className="ra-link">Abrir PDF / QR</a>
                )}
                <p className={`ra-hint ${isProd ? '' : 'ra-hint--danger'}`}>
                    {isProd
                        ? 'Envie o código ao pagador. Quando ele pagar, o valor cai na conta e aparece em Recebimentos.'
                        : 'Esta cobrança é de sandbox — não envie ao pagador, ela nunca será liquidada de verdade.'}
                </p>
            </div>
        );
    }

    return (
        <form className="ra-form" onSubmit={submit}>
            <label>Valor (R$)
                <input type="number" step="0.01" min="0.01" max={VALOR_MAX} value={f.amount} onChange={set('amount')} required />
            </label>
            <label>Nome do pagador
                <input type="text" maxLength={120} value={f.name} onChange={set('name')} required />
            </label>
            <label>CPF/CNPJ do pagador
                <input type="text" inputMode="numeric" maxLength={18} value={f.taxId} onChange={set('taxId')} required />
            </label>
            <label>Descrição (opcional)
                <input type="text" maxLength={255} value={f.description} onChange={set('description')} />
            </label>
            <button type="submit" className="ra-btn-primary ra-btn-block" disabled={creating}>
                {creating ? 'Gerando...' : 'Gerar cobrança Pix'}
            </button>
        </form>
    );
}

function BoletoCharge({ environment, scope, onDone, onBusy }) {
    const [f, setF] = useState({
        amount: '', name: '', taxId: '', due: '',
        zipCode: '', stateCode: '', streetLine1: '', streetLine2: '', district: '', city: '',
    });
    const [creating, setCreating] = useState(false);
    const [result, setResult] = useState(null);
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);
    useEffect(() => { onBusy?.(creating); }, [creating, onBusy]);

    const submit = async (e) => {
        e.preventDefault();
        if (creating) return;
        if (!escopoAindaVale(scope)) { avisarEscopoMudou(); return; }

        const valor = parseValor(f.amount, { min: BOLETO_MIN, max: VALOR_MAX });
        if (valor === null) {
            toast.error(`Boleto exige valor entre R$ ${BOLETO_MIN},00 e R$ ${VALOR_MAX.toLocaleString('pt-BR')}.`);
            return;
        }
        if (!f.name.trim()) { toast.error('Informe o nome do pagador.'); return; }
        if (!taxIdValido(f.taxId)) { toast.error('CPF/CNPJ do pagador inválido (11 ou 14 dígitos).'); return; }
        if (!vencimentoValido(f.due)) { toast.error('O vencimento não pode ser uma data passada.'); return; }

        // A Stark exige endereço completo do pagador no boleto — falhar aqui é
        // mais rápido e mais claro que receber a recusa dela.
        const uf = f.stateCode.trim().toUpperCase();
        const cep = onlyDigits(f.zipCode);
        if (!f.streetLine1.trim() || !f.city.trim() || !uf || !cep) {
            toast.error('O boleto exige o endereço do pagador (rua, cidade, UF e CEP).');
            return;
        }
        if (!UFS.has(uf)) { toast.error('UF inválida. Use a sigla de 2 letras (ex: SP).'); return; }
        if (cep.length !== 8) { toast.error('CEP inválido — informe os 8 dígitos.'); return; }

        setCreating(true);
        try {
            const { data } = await api.post('/api/baas/account/receiving-boleto', {
                amount: valor,
                name: f.name.trim(),
                tax_id: onlyDigits(f.taxId),
                due: f.due || undefined,
                address: {
                    streetLine1: f.streetLine1.trim(),
                    streetLine2: f.streetLine2.trim(),
                    district: f.district.trim(),
                    city: f.city.trim(),
                    stateCode: uf,
                    zipCode: cep,
                },
            });
            if (!aliveRef.current) return;
            setResult(data);
            onDone?.();
        } catch (e2) {
            const msg = apiErr(e2, 'Não foi possível gerar o boleto.');
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setCreating(false);
        }
    };

    if (result) {
        const isProd = environment === 'production';
        const linha = result.line || result.barCode || '';
        return (
            <div className="ra-result">
                <h4>Boleto gerado! {!isProd && <span className="ra-result-sbx">(teste)</span>}</h4>
                <span className="ra-result-amount">{fmtBRL(result.amount)}</span>
                <div className="ra-code-box">
                    <code>{linha || '—'}</code>
                    <button
                        className="ra-btn-ghost"
                        onClick={() => copiar(linha, isProd ? 'Linha digitável' : 'Linha digitável de TESTE')}
                        disabled={!linha}
                    >
                        Copiar
                    </button>
                </div>
                <p className={`ra-hint ${isProd ? '' : 'ra-hint--danger'}`}>
                    {isProd
                        ? `Vencimento: ${result.due || '—'}. Quando pago, o valor cai na conta e aparece em Recebimentos.`
                        : 'Boleto de sandbox — não envie ao pagador, ele não pode ser pago de verdade.'}
                </p>
            </div>
        );
    }

    return (
        <form className="ra-form" onSubmit={submit}>
            <div className="ra-form-row">
                <label>Valor (R$)
                    <input type="number" step="0.01" min={BOLETO_MIN} max={VALOR_MAX} value={f.amount} onChange={set('amount')} required />
                </label>
                <label>Vencimento
                    <input type="date" value={f.due} onChange={set('due')} min={new Date().toISOString().slice(0, 10)} />
                </label>
            </div>
            <label>Nome do pagador
                <input type="text" maxLength={120} value={f.name} onChange={set('name')} required />
            </label>
            <label>CPF/CNPJ do pagador
                <input type="text" inputMode="numeric" maxLength={18} value={f.taxId} onChange={set('taxId')} required />
            </label>

            <div className="ra-form-divider">Endereço do pagador (exigido no boleto)</div>
            <div className="ra-form-row">
                <label>CEP
                    <input type="text" inputMode="numeric" maxLength={9} value={f.zipCode} onChange={set('zipCode')} placeholder="01311-200" required />
                </label>
                <label>UF
                    <input
                        type="text"
                        maxLength={2}
                        value={f.stateCode}
                        onChange={(e) => setF((p) => ({ ...p, stateCode: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') }))}
                        placeholder="SP"
                        required
                    />
                </label>
            </div>
            <label>Rua e número
                <input type="text" maxLength={255} value={f.streetLine1} onChange={set('streetLine1')} required />
            </label>
            <label>Complemento
                <input type="text" maxLength={255} value={f.streetLine2} onChange={set('streetLine2')} />
            </label>
            <div className="ra-form-row">
                <label>Bairro
                    <input type="text" maxLength={120} value={f.district} onChange={set('district')} />
                </label>
                <label>Cidade
                    <input type="text" maxLength={120} value={f.city} onChange={set('city')} required />
                </label>
            </div>

            <button type="submit" className="ra-btn-primary ra-btn-block" disabled={creating}>
                {creating ? 'Gerando...' : 'Gerar boleto'}
            </button>
        </form>
    );
}